/* ============================================================
   Konnect GPS — homepage behaviour
   assets/kgps-home.js   (load with defer)

   Everything here is delegated from document, so it survives
   theme-editor section re-renders without re-binding.
   ============================================================ */
(function () {
  'use strict';

  /* ---- Theme toggle -------------------------------------- */
  function setTheme(mode) {
    document.documentElement.setAttribute('data-theme', mode);
    try { localStorage.setItem('kgps-theme', mode); } catch (e) {}
    document.querySelectorAll('[data-theme-set]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.themeSet === mode));
    });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-theme-set]');
    if (toggle) { setTheme(toggle.dataset.themeSet); return; }

    /* ---- Pricing plan toggle ----------------------------- */
    var opt = e.target.closest('.cost__opt');
    if (opt) {
      var panel = opt.closest('.cost__panel');
      var plan = opt.dataset.plan;
      panel.querySelectorAll('[data-plan-panel]').forEach(function (p) {
        p.hidden = p.dataset.planPanel !== plan;
      });
      panel.querySelectorAll('.cost__opt').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b === opt));
      });
      return;
    }

    /* ---- Video facade ------------------------------------ */
    var vid = e.target.closest('.vidMain, .vid');
    if (vid) {
      e.preventDefault();
      playVideo(vid);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var vid = e.target.closest('.vidMain, .vid');
    if (vid) { e.preventDefault(); playVideo(vid); }
  });

  /* Inject the iframe only on click. An eager YouTube embed
     costs ~700KB and wrecks LCP — never replace this with a
     plain <iframe> in the Liquid. */
  function playVideo(card) {
    var id = card.dataset.videoId;
    if (!id) return;
    var target = card.querySelector('.media') || card;
    var frame = document.createElement('iframe');
    frame.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1';
    frame.title = (card.querySelector('h3') || {}).textContent || 'Video';
    frame.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture';
    frame.allowFullscreen = true;
    frame.setAttribute('loading', 'lazy');
    frame.style.cssText =
      'width:100%;aspect-ratio:16/9;border:0;display:block;border-radius:16px';
    (card.classList.contains('vidMain') ? card : target).replaceWith(frame);
  }

  /* Sync toggle state on load and after theme-editor renders */
  function syncTheme() {
    var mode = document.documentElement.getAttribute('data-theme') || 'light';
    document.querySelectorAll('[data-theme-set]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.themeSet === mode));
    });
  }
  syncTheme();
  document.addEventListener('shopify:section:load', syncTheme);

  /* A toggle can be added by a section that renders after this
     script has already synced once (e.g. the KGPS Theme Switch
     section, or the theme editor inserting it live). Re-sync
     whenever a toggle appears that hasn't been initialised. */
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes.length &&
            document.querySelector('[data-theme-set]')) {
          syncTheme();
          break;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ============================================================
   Motion layer
   ------------------------------------------------------------
   Adds scroll reveal, a scrolled-state class, and a count-up on
   the ROI stat. Requires no changes to any section file — the
   selectors below decide what animates.

   Design constraints:
   · The hero and buy card are excluded on purpose. They are the
     LCP element and the primary CTA; fading them in delays
     perceived load and is measured by Core Web Vitals.
   · .kgps-anim is added to <html> here, and the hiding CSS is
     scoped to it. If this script fails, nothing is ever hidden.
   · IntersectionObserver, not a scroll listener — the browser
     does the work off the main thread.
   · Each element is unobserved once revealed. Nothing re-runs
     on scroll back up; repeated animation is irritating, not
     delightful.
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* What animates, and how. Anything inside .hero is skipped. */
  var TARGETS = [
    ['.sec__head', 'up'],
    ['.fork__card', 'up'],
    ['.trust__item', 'up'],
    ['.cost__grid > div:first-child', 'left'],
    ['.cost__panel', 'right'],
    ['.tblScroll', 'pop'],
    ['.install li', 'up'],
    ['.featBig__card', 'up'],
    ['.feat__item', 'up'],
    ['.aud__card', 'up'],
    ['.vidMain', 'pop'],
    ['.quote', 'up'],
    ['.ugc__card', 'up'],
    ['.b2b__grid > div:first-child', 'left'],
    ['.b2bform', 'right'],
    ['.tier', 'up'],
    ['.roi__grid > div', 'up'],
    ['.post', 'up'],
    ['.faq details', 'up'],
    ['.sec__more', 'up'],
    ['.installBar', 'up']
  ];

  var STAGGER = 70;   // ms between siblings
  var STAGGER_MAX = 5; // stop staggering after this many, or late
                       // items in a long grid feel broken

  function tag() {
    TARGETS.forEach(function (pair) {
      var nodes = document.querySelectorAll(pair[0]);
      var groups = new Map();

      nodes.forEach(function (el) {
        if (el.closest('.hero')) return;            // never the hero
        if (el.hasAttribute('data-reveal')) return; // already tagged
        el.setAttribute('data-reveal', pair[1] === 'up' ? '' : pair[1]);

        // Stagger relative to siblings, not to the whole page
        var parent = el.parentElement;
        var n = groups.get(parent) || 0;
        if (n > 0 && n <= STAGGER_MAX) {
          el.style.transitionDelay = (n * STAGGER) + 'ms';
        }
        groups.set(parent, n + 1);
      });
    });
  }

  function observe() {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, {
      // Fire slightly before the element reaches the viewport so
      // the motion finishes as it arrives, rather than starting then.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    });

    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      io.observe(el);
    });
  }

  /* Count up the ROI headline. Parses whatever currency or
     suffix is in the field and preserves it. */
  function countUp(el) {
    var text = el.textContent.trim();
    var match = text.match(/^([^\d-]*)(-?[\d,]+(?:\.\d+)?)(.*)$/);
    if (!match) return;

    var prefix = match[1];
    var target = parseFloat(match[2].replace(/,/g, ''));
    var suffix = match[3];
    var decimals = (match[2].split('.')[1] || '').length;
    if (!isFinite(target) || target === 0) return;

    var start = null;
    var DURATION = 1100;

    function frame(now) {
      if (start === null) start = now;
      var p = Math.min((now - start) / DURATION, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var value = (target * eased).toFixed(decimals);
      el.textContent = prefix + Number(value).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }) + suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function watchStat() {
    var stat = document.querySelector('.roi__stat');
    if (!stat) return;
    var original = stat.textContent;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        countUp(entry.target);
      });
    }, { threshold: 0.5 });
    io.observe(stat);

    // If anything goes wrong mid-count, put the real value back
    window.addEventListener('pagehide', function () {
      stat.textContent = original;
    });
  }

  /* Scrolled state: header shadow + sticky bar entrance */
  function watchScroll() {
    var ticking = false;
    function update() {
      root.classList.toggle('kgps-scrolled', window.scrollY > 120);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* Wrap everything after <summary> in .faq__body > div so the
     grid-rows collapse has a single wrapper with a single child.
     Richtext answers have several sibling elements, which is why
     the CSS-only version could not work. Runs regardless of
     reduced-motion — the wrapper is harmless, and the CSS
     transition is what gets disabled. */
  function wrapFaq() {
    document.querySelectorAll('.faq details').forEach(function (d) {
      if (d.querySelector(':scope > .faq__body')) return;

      var summary = d.querySelector(':scope > summary');
      if (!summary) return;

      var body = document.createElement('div');
      body.className = 'faq__body';
      var inner = document.createElement('div');
      body.appendChild(inner);

      var node = summary.nextSibling;
      while (node) {
        var next = node.nextSibling;
        inner.appendChild(node);
        node = next;
      }
      d.appendChild(body);
    });
  }

  /* Hero thumbnail carousel — swap the main photo on click */
  function heroGallery() {
    var main = document.getElementById('rhero-main');
    var thumbs = Array.prototype.slice.call(document.querySelectorAll('.rhero__thumb'));
    var dots = Array.prototype.slice.call(document.querySelectorAll('.rhero__dot'));
    if (!main || !thumbs.length) return;
    var idx = 0;

    function show(i) {
      i = (i + thumbs.length) % thumbs.length;
      if (i === idx && main.dataset.ready) return;
      idx = i;
      main.dataset.ready = '1';
      var t = thumbs[i];
      thumbs.forEach(function (x, k) {
        if (k === i) x.setAttribute('aria-current', 'true'); else x.removeAttribute('aria-current');
      });
      dots.forEach(function (d, k) {
        if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      main.classList.add('is-swapping');
      main.srcset = '';
      main.src = t.dataset.full;
      main.alt = t.dataset.alt || main.alt;
      var done = function () { main.classList.remove('is-swapping'); };
      main.addEventListener('load', done, { once: true });
      setTimeout(done, 400);
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { show(i); });
    });

    /* Phone: swipe the main photo. Threshold 40px, horizontal only,
       so a vertical scroll that starts on the photo is not hijacked. */
    var frame = main.closest('.rhero__frame');
    if (!frame) return;
    var sx = 0, sy = 0, tracking = false;
    frame.addEventListener('touchstart', function (e) {
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
    }, { passive: true });
    frame.addEventListener('touchend', function (e) {
      if (!tracking) return; tracking = false;
      var dx = e.changedTouches[0].clientX - sx;
      var dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      show(dx < 0 ? idx + 1 : idx - 1);
    }, { passive: true });
  }

  function init() {
    heroGallery();
    wrapFaq();
    watchScroll();

    if (reduced || !('IntersectionObserver' in window)) {
      // No reveal at all. Nothing was hidden, so nothing to undo.
      return;
    }
    root.classList.add('kgps-anim');
    tag();
    observe();
    watchStat();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-tag after a theme-editor section render, or newly added
     sections would sit invisible. */
  document.addEventListener('shopify:section:load', function () {
    heroGallery();
    wrapFaq();
    if (!root.classList.contains('kgps-anim')) return;
    tag();
    observe();
  });
})();
